
import { Button } from "@/components/ui/button";
import { ArrowLeft, Bitcoin, Coins, Wallet, Copy, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ChatOption from "@/components/ChatOption";
import { motion } from "framer-motion";
import { useIsMobile } from "@/hooks/use-mobile";

const CryptoBasics = () => {
  const isMobile = useIsMobile();
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F1F0FB] to-white">
      <Header />
      <div className="container mx-auto px-4 py-12">
        <div className="mb-8">
          <Link to="/knowledge-hub">
            <Button variant="ghost" className="flex items-center gap-2 hover:bg-[#9b87f5]/10 hover:text-[#9b87f5]">
              <ArrowLeft className="h-4 w-4" />
              Back to Knowledge Hub
            </Button>
          </Link>
        </div>

        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-[#4A607B] mb-6 relative inline-block">
              Crypto Basics
              <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 w-1/4 h-1 bg-corporate-secondary-100 rounded-full"></div>
            </h1>

            <p className="text-lg text-gray-600 mt-8 max-w-2xl mx-auto">
              Explore the fundamental concepts of cryptocurrency and blockchain technology through our comprehensive guides.
            </p>
          </motion.div>

          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate="show"
            className="grid gap-8"
          >
            {/* Copy Trading Section - Featured */}
            <motion.div variants={itemVariants}>
              <Link to="/knowledge-hub/basics/copy-trading" className="group">
                <div className="relative overflow-hidden rounded-xl">
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-purple-800/20 transform group-hover:scale-105 transition-transform duration-500 rounded-xl"></div>
                  <div className="bg-white/95 backdrop-blur-sm rounded-xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border border-purple-100/50 relative z-10">
                    <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
                      <div className="p-6 rounded-full bg-purple-50 group-hover:bg-purple-100 transition-colors">
                        <Copy className="h-12 w-12 text-purple-500" />
                      </div>
                      <div className="flex-1 text-center md:text-left">
                        <h2 className="text-3xl font-semibold text-[#4A607B] group-hover:text-purple-500 transition-colors mb-3">
                          Copy Trading
                        </h2>
                        <p className="text-gray-600 text-lg">
                          Learn how to automatically replicate the trades of experienced crypto traders and diversify your portfolio. Perfect for beginners and experienced traders alike.
                        </p>
                        <div className="mt-4 md:text-right">
                          <span className="inline-flex items-center text-purple-500 font-medium group-hover:translate-x-1 transition-transform duration-300">
                            Learn more <ArrowRight className="ml-2 h-4 w-4" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>

            <motion.div variants={itemVariants} className="grid md:grid-cols-3 gap-6 mt-4">
              {/* Bitcoin Section */}
              <Link to="/knowledge-hub/basics/bitcoin" className="group">
                <div className="bg-gradient-to-br from-orange-400/20 to-orange-600/20 p-0.5 rounded-xl overflow-hidden">
                  <div className="bg-white rounded-xl p-6 h-full shadow-lg hover:shadow-xl transition-all duration-300 transform group-hover:translate-y-[-2px]">
                    <div className="flex flex-col items-center text-center gap-4">
                      <div className="p-4 rounded-full bg-orange-50 group-hover:bg-orange-100 transition-colors">
                        <Bitcoin className="h-8 w-8 text-orange-500" />
                      </div>
                      <h2 className="text-xl font-semibold text-[#4A607B] group-hover:text-orange-500 transition-colors">Bitcoin</h2>
                      <p className="text-gray-600">
                        Learn about the first and most influential cryptocurrency.
                      </p>
                      <div className="mt-4 w-full pt-4 border-t border-gray-100">
                        <span className="inline-flex items-center text-orange-500 text-sm font-medium group-hover:translate-x-1 transition-transform duration-300">
                          Discover <ArrowRight className="ml-1 h-3 w-3" />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>

              {/* Cryptocurrency Section */}
              <Link to="/knowledge-hub/basics/cryptocurrency" className="group">
                <div className="bg-gradient-to-br from-blue-400/20 to-blue-600/20 p-0.5 rounded-xl overflow-hidden">
                  <div className="bg-white rounded-xl p-6 h-full shadow-lg hover:shadow-xl transition-all duration-300 transform group-hover:translate-y-[-2px]">
                    <div className="flex flex-col items-center text-center gap-4">
                      <div className="p-4 rounded-full bg-blue-50 group-hover:bg-blue-100 transition-colors">
                        <Coins className="h-8 w-8 text-blue-500" />
                      </div>
                      <h2 className="text-xl font-semibold text-[#4A607B] group-hover:text-blue-500 transition-colors">Cryptocurrency</h2>
                      <p className="text-gray-600">
                        Discover how digital assets are revolutionizing finance.
                      </p>
                      <div className="mt-4 w-full pt-4 border-t border-gray-100">
                        <span className="inline-flex items-center text-blue-500 text-sm font-medium group-hover:translate-x-1 transition-transform duration-300">
                          Discover <ArrowRight className="ml-1 h-3 w-3" />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>

              {/* Wallets Section */}
              <Link to="/knowledge-hub/basics/wallets" className="group">
                <div className="bg-gradient-to-br from-green-400/20 to-green-600/20 p-0.5 rounded-xl overflow-hidden">
                  <div className="bg-white rounded-xl p-6 h-full shadow-lg hover:shadow-xl transition-all duration-300 transform group-hover:translate-y-[-2px]">
                    <div className="flex flex-col items-center text-center gap-4">
                      <div className="p-4 rounded-full bg-green-50 group-hover:bg-green-100 transition-colors">
                        <Wallet className="h-8 w-8 text-green-500" />
                      </div>
                      <h2 className="text-xl font-semibold text-[#4A607B] group-hover:text-green-500 transition-colors">Crypto Wallets</h2>
                      <p className="text-gray-600">
                        Understand how to securely store your digital assets.
                      </p>
                      <div className="mt-4 w-full pt-4 border-t border-gray-100">
                        <span className="inline-flex items-center text-green-500 text-sm font-medium group-hover:translate-x-1 transition-transform duration-300">
                          Discover <ArrowRight className="ml-1 h-3 w-3" />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>
      <Footer />
      {!isMobile && <ChatOption />}
    </div>
  );
};

export default CryptoBasics;
